/**
 * useDriverSyncHealth — 30-second poll for driver sync status.
 *
 * Phase 9 / Plan 09-05 — polls GET /v1/drivers/sync-health and provides the data
 * to DriverStack for rendering the green/amber/red sync badge per driver (DISPATCH-03 / D-04).
 *
 * Poll cadence: 30 seconds (refetchInterval: 30_000).
 * Same options as useDriverPositions for consistency.
 *
 * Spec: PATTERNS.md (sync-health analogue); RESEARCH Pattern 6; DISPATCH-03 / D-04.
 */

import { useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { DriverSyncHealth } from '@lodex-pro/shared';

/** TanStack Query cache key for the driver sync-health endpoint. */
export const DRIVER_SYNC_HEALTH_QUERY_KEY = ['drivers', 'sync-health'] as const;

/** Response envelope from GET /v1/drivers/sync-health. */
interface DriverSyncHealthResponse {
  readonly drivers: readonly DriverSyncHealth[];
}

/**
 * Polls GET /v1/drivers/sync-health every 30 seconds.
 *
 * Returns standard TanStack Query result: `{ data, isLoading, isFetching, isError }`.
 * Use `isFetching` (not `isLoading`) for the "Refreshing..." text indicator after
 * the initial load.
 */
export function useDriverSyncHealth(): ReturnType<typeof useApiQuery<DriverSyncHealthResponse>> {
  const opts = useApiOpts();
  return useApiQuery<DriverSyncHealthResponse>(opts, '/v1/drivers/sync-health', {
    queryKey: DRIVER_SYNC_HEALTH_QUERY_KEY,
    refetchInterval: 30_000,
    staleTime: 25_000,
    refetchIntervalInBackground: false,
  });
}
